import { EntityAnnotationDto } from "../../annotation/interfaces/annotation";

/**
 * Normalization Batch Coordinator
 *
 * Collects normalization requests and batches them together to reduce API calls.
 * Instead of making N individual normalization calls, we debounce and make 1 batch call.
 *
 * This significantly improves performance when multiple annotations are created/deleted
 * in quick succession.
 */

type NormalizationRequest = {
  annotation: EntityAnnotationDto;
  timestamp: number;
};

// Module-level state for batching across all hook instances
const normalizationBatch = new Map<string, NormalizationRequest>();
let batchTimeout: NodeJS.Timeout | null = null;
let batchCallback: ((annotations: EntityAnnotationDto[]) => void) | null = null;

// Debounce window in milliseconds
const BATCH_DEBOUNCE_MS = 150;

/**
 * Initialize the batch coordinator with a callback function
 */
export const initNormalizationBatchCoordinator = (
  callback: (annotations: EntityAnnotationDto[]) => void,
) => {
  batchCallback = callback;
};

/**
 * Add an annotation to the normalization batch
 */
export const scheduleNormalization = (annotation: EntityAnnotationDto) => {
  if (!batchCallback) {
    console.warn("Normalization batch coordinator not initialized");
    return;
  }

  // Add to batch (using annotation ID as key to prevent duplicates)
  normalizationBatch.set(annotation.id, {
    annotation,
    timestamp: Date.now(),
  });

  // Clear existing timeout
  if (batchTimeout) {
    clearTimeout(batchTimeout);
  }

  // Schedule batch processing
  batchTimeout = setTimeout(() => {
    processBatch();
  }, BATCH_DEBOUNCE_MS);
};

/**
 * Process the current batch of normalization requests
 */
const processBatch = () => {
  if (normalizationBatch.size === 0) {
    return;
  }

  // Extract annotations from batch
  const annotations = Array.from(normalizationBatch.values()).map(
    (req) => req.annotation,
  );

  // Clear batch
  normalizationBatch.clear();
  batchTimeout = null;

  // Execute batch callback
  if (batchCallback) {
    batchCallback(annotations);
  }
};

/**
 * Force immediate processing of pending batch (useful for cleanup)
 */
export const flushNormalizationBatch = () => {
  if (batchTimeout) {
    clearTimeout(batchTimeout);
    batchTimeout = null;
  }
  processBatch();
};

/**
 * Clear the batch without processing (useful for cleanup)
 */
export const clearNormalizationBatch = () => {
  if (batchTimeout) {
    clearTimeout(batchTimeout);
    batchTimeout = null;
  }
  normalizationBatch.clear();
};

/**
 * Get current batch size (useful for debugging/testing)
 */
export const getNormalizationBatchSize = () => normalizationBatch.size;
